import React from "react";
import { ErrorPage } from "./ErrorPage";

type ErrorBoundaryProps = {
  children: React.ReactNode;
};

type ErrorBoundaryState = {
  error: Error | null;
};

/**
 * Catches runtime render errors anywhere below it and shows the themed
 * ErrorPage instead of a white screen. Wraps the whole app in App.jsx.
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo) {
    // oxlint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info.componentStack);

    // Stale chunk after deploy: reload once to fetch fresh chunks.
    // Message wording differs per browser (Chrome: "Failed to fetch dynamically
    // imported module", Firefox: "error loading dynamically imported module",
    // Safari: "Importing a module script failed") — match all of them.
    const isChunkError =
      error.message.includes("dynamically imported module") ||
      error.message.includes("Importing a module script failed") ||
      error.name === "ChunkLoadError";
    if (isChunkError) {
      const RELOAD_KEY = "chunk-reload-attempted";
      if (!sessionStorage.getItem(RELOAD_KEY)) {
        sessionStorage.setItem(RELOAD_KEY, "1");
        window.location.reload();
      }
    }
  }

  handleReset = () => {
    this.setState({ error: null });
  };

  override render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <ErrorPage
        code="500"
        title="Đã có lỗi xảy ra"
        subtitle="Ứng dụng gặp sự cố không mong muốn. Bạn có thể thử lại hoặc về trang chủ."
        detail={
          import.meta.env.DEV ? `${error.name}: ${error.message}\n${error.stack ?? ""}` : undefined
        }
        onRetry={this.handleReset}
      />
    );
  }
}
